export type SeatingLayout = {
  classId: number; rows: number; cols: number; rowAisles: number[]; colAisles: number[];
  seats: Record<string, number>; version: number;
};

/** Do not replace a saved layout or draft with a partial/mis-scoped response. */
export function parseSeatingLayout(value: unknown, classId: number): SeatingLayout {
  const invalid = () => new Error("座位表响应不完整或不属于当前班级，请重新载入确认；草稿已保留");
  if (!value || typeof value !== "object") throw invalid();
  const layout = value as SeatingLayout;
  const integer = (n: unknown, min: number, max: number): n is number =>
    typeof n === "number" && Number.isInteger(n) && n >= min && n <= max;
  if (layout.classId !== classId || !integer(layout.rows, 1, 12) || !integer(layout.cols, 1, 12) ||
      !integer(layout.version, 0, 2147483647)) throw invalid();
  for (const [values, size] of [[layout.rowAisles, layout.rows], [layout.colAisles, layout.cols]] as const) {
    if (!Array.isArray(values) || values.some((n) => !integer(n, 1, size - 1)) || new Set(values).size !== values.length) throw invalid();
  }
  if (!layout.seats || typeof layout.seats !== "object" || Array.isArray(layout.seats)) throw invalid();
  const ids = new Set<number>();
  for (const [key, id] of Object.entries(layout.seats)) {
    if (!/^(0|[1-9]\d*)-(0|[1-9]\d*)$/.test(key) || !integer(id, 1, 2147483647) || ids.has(id)) throw invalid();
    const [row, col] = key.split("-").map(Number);
    if (row >= layout.rows || col >= layout.cols) throw invalid();
    ids.add(id);
  }
  return layout;
}

export function moveStudent(layout: SeatingLayout, studentId: number, target: string): SeatingLayout {
  const seats = { ...layout.seats };
  const source = Object.keys(seats).find((key) => seats[key] === studentId);
  if (source === target) return layout;
  const displaced = seats[target];
  if (source) delete seats[source];
  if (target !== "unassigned") {
    seats[target] = studentId;
    if (source && displaced) seats[source] = displaced;
  }
  return { ...layout, seats };
}

export function resizeSeating(layout: SeatingLayout, rows: number, cols: number): SeatingLayout {
  return { ...layout, rows, cols, rowAisles: layout.rowAisles.filter((r) => r < rows),
    colAisles: layout.colAisles.filter((c) => c < cols),
    seats: Object.fromEntries(Object.entries(layout.seats).filter(([key]) => {
      if (!/^(0|[1-9]\d*)-(0|[1-9]\d*)$/.test(key)) return false;
      const [r, c] = key.split("-").map(Number);
      return r < rows && c < cols;
    })) };
}
